// Uncomment and update imports as needed

import { MultiDirectedGraph } from 'graphology'
import type { Attributes } from 'graphology-types'
import { Cnx, Obj, EangEventTypes } from './entity.js'
import { createObj, PersonObj, PolicyRuleObj, ServiceAccountObj } from './objects.js'
import { Prettify } from './types.js'

export type AuthzActionType = Prettify<EangEventTypes | 'read'>

export interface AuthzRequest {
  subject: PersonObj | ServiceAccountObj
  action: AuthzActionType
  entity: Obj | Cnx
  environment?: Environment
}

export type RuleEffect = (typeof RuleEffect)[keyof typeof RuleEffect] | undefined
export const RuleEffect = {
  allow: 'allow',
  deny: 'deny'
} as const

export interface AuthzResult {
  effect: RuleEffect
  matchedRule?: PolicyRuleObj
  entity: Obj | Cnx
}

// Operation of a Condition
type Op = 'equals' | 'in' | 'hasConnectionTo'

// Value can be a literal or a reference to another property
type ValueRef = {
  ref: EntityPath // e.g., "entity.fromObjId", "subject.id"
}

type Value = string | number | boolean | string[] | ValueRef

// Entity or Property paths that can be used in conditions
type EntityPath = string

type Condition = {
  [K in EntityPath]?: {
    op: Op
    value?: Value // Value for the operation. Either value or ref must be provided.
    ref?: EntityPath // Optional reference to another property for comparison
    typeOf?: string[] // Used when the Op is "hasConnectionTo"
    negate?: boolean // Whether to negate the result
  }
}

export type Rule = {
  description?: string
  conditions: Condition[]
}

export class Authorizer {
  constructor(public graph = new MultiDirectedGraph()) {}

  getPolicyRuleChain(entity: Obj | Cnx) {
    const collectedPolicies: string[] = []

    // Start from the entity and traverse up following "child_of" edges.
    // If the entity is a Cnx, start from its "child_of" target.
    let currentNodeId: string | undefined

    if (entity instanceof Cnx) {
      currentNodeId = entity.childOf
    } else {
      currentNodeId = entity.id
    }

    // Traverse up the childOf chain
    while (currentNodeId && this.graph.hasNode(currentNodeId)) {
      // Collect SecurityPolicy nodes that have child_of edges TO current node
      this.graph.forEachInEdge(
        currentNodeId,
        (_edgeId: string, attributes: Attributes, source: string, _target: string) => {
          if (attributes.entity.typeOf === 'child_of') {
            const sourceEntity = this.graph.getNodeAttribute(source, 'entity')
            if (sourceEntity.typeOf === 'SecurityPolicy') {
              collectedPolicies.push(source)
            }
          }
        }
      )

      // Move to parent
      // TODO: This should be a DAG and cycles should not exist. Re-evaluate.
      const currentEntity = this.graph.getNodeAttribute(currentNodeId, 'entity')
      if (currentEntity.childOf === undefined) {
        break
      } else {
        currentNodeId = currentEntity.childOf
      }
    }

    // Collect policy rules from all collected policies
    const policyRuleChain: PolicyRuleObj[] = []

    collectedPolicies.reverse().forEach((policyId) => {
      this.graph.forEachInEdge(
        policyId,
        (_edgeId: string, attributes: Attributes, source: string, _target: string) => {
          if (attributes.entity.typeOf === 'rule_for') {
            const ruleEntity = this.graph.getNodeAttribute(source, 'entity')
            if (ruleEntity.typeOf === 'PolicyRule') {
              policyRuleChain.push(createObj(ruleEntity) as PolicyRuleObj)
            }
          }
        }
      )
    })

    return policyRuleChain
  }

  authorize(authzRequest: AuthzRequest) {
    const { entity, action } = authzRequest
    // If a new entity is being created, use the childOf entity as a starting point
    let policyStartEntity = entity
    if (action === 'create') {
      // policyStartEntity might be the root
      if (entity.childOf && this.graph.hasNode(entity.childOf)) {
        policyStartEntity = this.graph.getNodeAttribute(entity.childOf, 'entity')
      } else if (
        !entity.childOf &&
        entity.instanceOfId &&
        this.graph.hasNode(entity.instanceOfId)
      ) {
        policyStartEntity = this.graph.getNodeAttribute(entity.instanceOfId, 'entity')
      }
    }
    const policyRuleChain = this.getPolicyRuleChain(policyStartEntity)
    if (!policyRuleChain || policyRuleChain.length === 0) {
      return { effect: RuleEffect.deny, entity } as AuthzResult
    }
    const response = this.applyChain(authzRequest, policyRuleChain)
    return response.effect === RuleEffect.deny
      ? this.applyChain(authzRequest, policyRuleChain)
      : response
  }

  hasConnectionTo(fromId: string, toId: string, connectionType: string): boolean {
    // Check if a directed edge exists from fromId to toId with matching type
    let found = false

    this.graph.forEachEdge(fromId, toId, (_edgeId: string, attributes: Attributes) => {
      if (attributes.entity.typeOf === connectionType) {
        found = true
      }
    })

    return found
  }

  /**
   * Resolves a property path from the authorization request context
   * @param path - Property path like "entity.id", "subject.id", "entity.fromObjId", "action"
   * @param authzRequest - The authorization request context
   * @returns The resolved value or undefined if not found
   */
  private resolvePropertyPath(path: string, authzRequest: AuthzRequest): unknown {
    const parts = path.split('.')
    let current: any = authzRequest

    for (const part of parts) {
      if (current === undefined || current === null) {
        return undefined
      }
      current = current[part]
    }

    return current
  }

  /**
   * Evaluates a single condition against the authorization request
   * @param condition - The condition to evaluate
   * @param authzRequest - The authorization request context
   * @returns true if the condition matches, false otherwise
   */
  private evaluateCondition(condition: Condition, authzRequest: AuthzRequest): boolean {
    // Each condition object has one property path as the key
    const entries = Object.entries(condition)
    if (entries.length !== 1) {
      return false
    }

    const entry = entries[0]
    if (!entry) {
      return false
    }

    const [propertyPath, conditionDef] = entry
    if (!conditionDef) {
      return false
    }

    const actualValue = this.resolvePropertyPath(propertyPath, authzRequest)
    const { op, value, ref, typeOf, negate } = conditionDef

    let result = false

    switch (op) {
      case 'equals': {
        const compareValue = ref ? this.resolvePropertyPath(ref, authzRequest) : value
        result = actualValue === compareValue
        break
      }

      case 'in': {
        if (Array.isArray(value)) {
          result = value.includes(actualValue as string)
        }
        break
      }

      case 'hasConnectionTo': {
        // For hasConnectionTo, the propertyPath should be 'subject' or similar
        // and we need to check if subject has a connection to the target
        const subjectId = (actualValue as any)?.id
        const targetId = value as string

        if (subjectId && targetId && typeOf && Array.isArray(typeOf)) {
          // Check if any of the connection types match
          result = typeOf.some((connectionType) =>
            this.hasConnectionTo(subjectId, targetId, connectionType)
          )
        }
        break
      }

      default:
        result = false
    }

    // Apply negation if specified
    return negate ? !result : result
  }

  /**
   * Evaluates an array of rules against the authorization request
   * All conditions within a rule must match (AND logic)
   * Any rule matching returns true (OR logic between rules)
   * @param rules - Array of rules to evaluate
   * @param authzRequest - The authorization request context
   * @returns true if any rule matches, false otherwise
   */
  evaluateRules(rules: Rule[], authzRequest: AuthzRequest): boolean {
    // OR logic between rules - if any rule matches, return true
    for (const rule of rules) {
      // AND logic between conditions - all must match for the rule to match
      const allConditionsMatch = rule.conditions.every((condition) =>
        this.evaluateCondition(condition, authzRequest)
      )

      if (allConditionsMatch) {
        return true
      }
    }

    return false
  }

  private applyChain(accessRequest: AuthzRequest, policyRuleChain: PolicyRuleObj[]) {
    const response = { effect: RuleEffect.deny } as AuthzResult

    const filteredPolicyRules = policyRuleChain.filter((rule) => {
      const actionMatch =
        !rule.applicableActions || rule.applicableActions.includes(accessRequest.action)
      if (!actionMatch) return false
      const resourceTypeMatch =
        !rule.applicableEntityTypes ||
        rule.applicableEntityTypes.includes(accessRequest.entity.typeOf)
      return actionMatch && resourceTypeMatch
    })

    for (const rule of filteredPolicyRules) {
      // Check if the rule has a declarative rules array
      const matched = rule.rules ? this.evaluateRules(rule.rules, accessRequest) : false

      if (matched) {
        response.effect = rule.effect
        response.matchedRule = rule
        response.entity = accessRequest.entity

        if (rule.overrideable === false) {
          break
        }
      }
    }

    return response
  }
}

export class Environment {
  // Time-based attributes
  timeOfDay?: string
  timeRange?: { start: string; end: string }
  dayOfWeek?: string
  date?: Date
  isHoliday?: boolean

  // Location-based attributes
  ipAddress?: string
  geographicLocation?: {
    country?: string
    region?: string
    city?: string
  }
  networkZone?: 'internal' | 'DMZ' | 'external'
  connectionType?: 'VPN' | 'direct' | 'wireless' | string

  // System/Device attributes
  deviceType?: 'company-managed' | 'BYOD' | 'mobile' | 'desktop' | string
  securityPosture?: {
    patchLevel?: string
    antivirusStatus?: 'active' | 'inactive' | string
    encryptionStatus?: 'enabled' | 'disabled' | string
  }
  operatingSystem?: string
  osVersion?: string
  browserType?: string
  browserVersion?: string
  certificateStatus?: 'valid' | 'expired' | 'missing' | string

  // Risk-based attributes
  threatLevel?: 'low' | 'medium' | 'high' | 'critical' | string
  anomalyScore?: number
  previousAuthFailures?: number
  sessionCharacteristics?: {
    duration?: number
    idleTime?: number
  }

  // Organizational context
  businessProcess?: string
  // (disaster recovery mode)
  emergencyStatus?: boolean
  // (restrict non-essential access during high load)
  systemLoad?: number
  systemHealth?: 'normal' | 'degraded' | 'critical' | string
  maintenanceWindow?: boolean

  // Compliance context
  dataClassificationLevel?: 'public' | 'internal' | 'confidential' | 'restricted' | string
  regulatoryJurisdiction?: string[]
  underAudit?: boolean

  constructor(
    public name: string,
    public description?: string,
    attributes?: Partial<Omit<Environment, 'name' | 'description'>>
  ) {
    // Apply any provided attributes
    if (attributes) {
      Object.assign(this, attributes)
    }
  }
}

// export class AuthzAction {
//   private constructor(
//     public type: AuthzActionType,
//     public data: Record<string, unknown> = {}
//   ) {}

//   static create(data: Record<string, unknown>): AuthzAction {
//     return new AuthzAction(EangEventType.create, data)
//   }

//   static update(data: Record<string, unknown>): AuthzAction {
//     return new AuthzAction(EangEventType.update, data)
//   }

//   static delete(): AuthzAction {
//     return new AuthzAction(EangEventType.delete)
//   }

//   static start(): AuthzAction {
//     return new AuthzAction(EangEventType.start)
//   }

//   static stop(): AuthzAction {
//     return new AuthzAction(EangEventType.stop)
//   }

//   static read(): AuthzAction {
//     return new AuthzAction('read')
//   }
// }
